import {
  Component,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  PayoutDAO,
  StripeTransferInfo,
} from '../../../../core/services/dao/payoutDAO';
import { Payout } from 'anchor-shared/lib/model/entity/payout';
import { SubPayout } from 'anchor-shared/lib/model/entity/subPayout';
import { forkJoin, Subscription } from 'rxjs';
import { unsubscribeFromAll } from '../../../../core/pure-functions/rx-helper';
import { ContainsSubscriptions } from '../../../../core/services/interfaces/contains-subscriptions';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PaystubDAO } from '../../../../core/services/dao/paystubDAO';
import { ConstantMan } from '../../../../core/services/constantMan';
import { SubPayoutDAO } from '../../../../core/services/dao/subPayoutDAO';
import { UserDAO } from '../../../../core/services/dao/userDAO';
import { User } from 'anchor-shared/lib/model/entity/user';
import { DialogsService } from '../../../../core/services/dialog.service';
import { UserPaymentDetail } from 'anchor-shared/lib/model/entity/user-payment-detail';
import { environment } from '../../../../environments/environment';
import { ChickenTestDialogService } from '../../../shared/dialogs/chicken-test-dialog/chicken-test-dialog.service';
import { take } from 'rxjs/operators';
import { Charter } from 'anchor-shared/lib/model/entity/charter';
import { SystemPayoutHold } from 'anchor-shared/lib/model/enum/systemPayoutHoldType';

@Component({
  selector: 'app-unpaid-charters',
  templateUrl: './unpaid-charters.component.html',
  styleUrls: ['./unpaid-charters.component.scss'],
})
export class UnpaidChartersComponent
  implements OnInit, OnDestroy, ContainsSubscriptions
{
  payouts: Payout[];
  filteredPayouts: PayoutOrSubPayout[] = [];
  subs: Subscription[] = [];
  disabledPayouts = [];
  ConstantMan: ConstantMan;
  Base: number;
  GetTotalFromBase: number;
  payoutsWithSubPayouts: PayoutOrSubPayout[] = [];
  payoutsWithSubPayoutsOnAdminHold: PayoutOrSubPayout[] = [];
  unpaidUsers: UserWithChartersAndPayouts[] = [];
  unpaidUserIds: number[];
  isProd: boolean = true;
  stripeBalance: number = 0.0;
  readyToPay: number = 0.0;
  payInProgress: boolean = false;
  topUpUrl: string;
  payoutBeingEdited = null;
  bulkActionInProgress: boolean = false;
  loading: boolean = true;

  SystemPayoutHold = SystemPayoutHold;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private payoutDao: PayoutDAO,
    private subPayoutDao: SubPayoutDAO,
    private paystubDao: PaystubDAO,
    private userDao: UserDAO,
    private snackBar: MatSnackBar,
    private dialogService: DialogsService,
    @Inject(PLATFORM_ID) private platformId: any,
    private chickenTestDialog: ChickenTestDialogService,
  ) {
    if (!environment.production) {
      this.isProd = false;
    }
    this.loadPayouts();
    this.getStripeBalance();
    this.Base = ConstantMan.PROCESSING_FEE.BASE;
    this.GetTotalFromBase = ConstantMan.PROCESSING_FEE.GET_TOTAL_FROM_BASE;
    this.topUpUrl = this.isProd
      ? 'https://dashboard.stripe.com/topups'
      : 'https://dashboard.stripe.com/test/topups';
  }

  ngOnInit() {}

  loadPayouts(): void {
    const loadSub = this.payoutDao.loadForProcessing().subscribe((payouts) => {
      this.payouts = payouts;
      this.addSubPayouts(payouts);
    });
    this.subs.push(loadSub);
  }

  ngOnDestroy(): void {
    unsubscribeFromAll(this.subs);
  }

  addSubPayouts(payouts) {
    payouts.map((payoutsWithSubPayouts: PayoutOrSubPayout) => {
      if (payoutsWithSubPayouts.subPayouts.length === 0) {
        if (payoutsWithSubPayouts.netPay === null || 'NaN') {
          if (payoutsWithSubPayouts.processingFee) {
            payoutsWithSubPayouts.netPay = (
              parseFloat(payoutsWithSubPayouts.actualPay) *
              this.GetTotalFromBase
            ).toFixed(2);
          } else {
            payoutsWithSubPayouts.netPay = parseFloat(
              payoutsWithSubPayouts.actualPay,
            ).toFixed(2);
          }
        }
        if (
          payoutsWithSubPayouts.adminHold ||
          payoutsWithSubPayouts.systemHold
        ) {
          this.payoutsWithSubPayoutsOnAdminHold.push(payoutsWithSubPayouts);
        } else {
          this.payoutsWithSubPayouts.push(payoutsWithSubPayouts);
        }
      } else {
        this.createPayoutLikeObjectsFromSubPayouts(payoutsWithSubPayouts);
      }
    });
    this.initPayouts();
  }

  initPayouts() {
    this.createUnpaidPayoutsByUserMap();
  }

  getStripeBalance() {
    const balanceSub = this.payoutDao
      .getStripeBalance()
      .subscribe((balance) => {
        if (balance.getBalanceSuccessful) {
          this.stripeBalance =
            balance.stripeResponseBody.available[0].amount / 100;
        }
      });
    this.subs.push(balanceSub);
  }

  payAllWithStripe() {
    this.bulkActionInProgress = true;
    const bulkIds = [];
    const payableUsers = this.unpaidUsers.filter((user) => {
      if (
        user.stripeConnectComplete &&
        user.userPaymentDetails &&
        user.userPaymentDetails.stripeUserId !== null
      ) {
        bulkIds.push(user.id);
        this.readyToPay -= user.netPay;
        return true;
      }
      return false;
    });

    if (!payableUsers || (payableUsers && !payableUsers.length)) {
      this.openSnackbar(
        'There are no payouts ready to be paid. If you think you are seeing this message when you should not be, please contact IT.',
      );
      this.bulkActionInProgress = false;
    } else {
      const stripeTransferInfo: StripeTransferInfo[] = payableUsers.map(
        (user) => {
          const newPayouts: Partial<Payout>[] = [];
          const newSubPayouts: Partial<SubPayout>[] = [];

          user.allUserPayouts.map((payout) => {
            if (payout.paidAtTheDock) {
              if (parseFloat(payout.actualPay) < payout.prePaymentAmount) {
                // Remove money from stripe
                const transferReversal = this.payoutDao
                  .reverseStripeTransfer(
                    payout.transferId,
                    payout.prePaymentAmount - parseFloat(payout.actualPay),
                  )
                  .subscribe(() => {});
                this.subs.push(transferReversal);
                user.netPay -= parseFloat(payout.actualPay);
              } else {
                user.netPay -= payout.prePaymentAmount;
                if (parseFloat(payout.basePay) - payout.prePaymentAmount > 0) {
                  if (!payout.isSubPayout) {
                    let newPayoutIds = newPayouts.map((payout) => payout.id);
                    if (!newPayoutIds.includes(payout.id)) {
                      newPayouts.push({
                        id: payout.id,
                        payee: payout.payee,
                        name: payout.name,
                        type: payout.type,
                        charterId: payout.charterId,
                        charterDate: payout.charterDate,
                        basePay: `${
                          parseFloat(payout.basePay) - payout.prePaymentAmount
                        }`,
                        actualPay: `${
                          parseFloat(payout.basePay) - payout.prePaymentAmount
                        }`,
                        payeePaypalHandle: payout.payeePaypalHandle,
                        paid: true,
                        taxPercentage: payout.taxPercentage,
                        markupPercentage: payout.markupPercentage,
                        published: true,
                        processingFee: false,
                        netPay: null,
                        userId: payout.userId,
                        crewId: payout.crewId,
                        ownerId: payout.ownerId,
                        systemNotes: payout.systemNotes,
                        notes: 'additional hour pay - ' + payout.notes,
                      });
                    }
                  } else {
                    let newSubPayoutIds = newSubPayouts.map(
                      (subPayout) => subPayout.id,
                    );
                    if (!newSubPayoutIds.includes(payout.id)) {
                      newSubPayouts.push({
                        id: payout.id,
                        parentId: payout.parentId,
                        userId: payout.userId,
                        basePay: `${
                          parseFloat(payout.basePay) - payout.prePaymentAmount
                        }`,
                        actualPay: `${
                          parseFloat(payout.basePay) - payout.prePaymentAmount
                        }`,
                        notes: 'additional hour pay - ' + payout.notes,
                        published: true,
                        paid: true,
                        // @ts-ignore
                        type: payout.type,
                        processingFee: false,
                      });
                    }
                  }
                  payout.basePay = payout.actualPay =
                    payout.prePaymentAmount.toString();
                }
              }
            }
          });

          return {
            userId: user.id,
            stripeUserId: user.userPaymentDetails.stripeUserId,
            payout: user.allUserPayouts
              .filter((payout) => !payout.isSubPayout)
              .map((payout) => {
                return {
                  id: payout.id,
                  paidDate: payout.paidDate,
                  paidAtTheDock: payout.paidAtTheDock,
                  basePay: payout.basePay,
                };
              }),
            newPayouts: newPayouts,
            subPayout: user.allUserPayouts
              .filter((payout) => payout.isSubPayout)
              .map((payout) => {
                return {
                  id: payout.id,
                  paidDate: payout.paidDate,
                  paidAtTheDock: payout.paidAtTheDock,
                  basePay: payout.basePay,
                };
              }),
            newSubPayouts: newSubPayouts,
            netPay: user.netPay,
          };
        },
      );

      this.payoutDao
        .bulkPayWithStripe(stripeTransferInfo, payableUsers)
        .pipe(take(1))
        .subscribe((success) => {
          if (success) {
            this.getStripeBalance();
            this.unpaidUsers = [];
            this.payoutsWithSubPayouts = [];
            this.payoutsWithSubPayoutsOnAdminHold = [];
            this.payouts = [];
            this.loadPayouts();
            this.bulkActionInProgress = false;
            this.openSnackbar('Bulk payout complete');
          } else {
            this.openSnackbar(
              'Error completing bulk payout. Please contact IT.',
            );
          }
        });
    }
  }

  sendAllEmailRequests() {
    const usersToEmail = this.unpaidUsers.filter((user) => {
      return !user.stripeConnectComplete;
    });

    for (let user of usersToEmail) {
      this.sendStripeSignUpEmail(user);
    }
    this.openSnackbar('Emailed stripe signup to all users');
  }

  disableStripePayAll(): boolean {
    return this.stripeBalance < this.readyToPay;
  }

  payWithStripe(user: UserWithChartersAndPayouts) {
    user.allUserPayouts[0].paid = true;
    user.allUserPayouts[0].paidDate = new Date();

    user.allUserPayouts.map((payout) => {
      if (payout.paidAtTheDock) {
        if (parseFloat(payout.actualPay) < payout.prePaymentAmount) {
          // Remove money from stripe
          const transferReversal = this.payoutDao
            .reverseStripeTransfer(
              payout.transferId,
              payout.prePaymentAmount - parseFloat(payout.actualPay),
            )
            .subscribe(() => {});
          this.subs.push(transferReversal);
          user.netPay -= parseFloat(payout.actualPay);
        } else {
          user.netPay -= payout.prePaymentAmount;
        }
      }
    });
    if (user.netPay <= 0) {
      this.markAsPaid(user);
      this.sendPaystubAndUpdatePayouts(user);
      const userIndex = this.unpaidUsers.findIndex(
        (found) => found.id === user.id,
      );
      this.unpaidUsers.splice(userIndex, 1);
    } else {
      const payoutSub = this.payoutDao
        .payWithStripe(user.userPaymentDetails.stripeUserId, user.netPay)
        .subscribe((stripeResponse) => {
          if (stripeResponse.paymentSuccessful) {
            this.markAsPaid(user);
            this.sendPaystubAndUpdatePayouts(user);
            const userIndex = this.unpaidUsers.findIndex(
              (found) => found.id === user.id,
            );
            this.unpaidUsers.splice(userIndex, 1);
          } else if (
            !stripeResponse.paymentSuccessful &&
            stripeResponse.stripeResponseBody.code === 'balance_insufficient'
          ) {
            user.allUserPayouts[0].paid = false;
            user.allUserPayouts[0].paidDate = null;
            const insufficientSub = this.dialogService
              .stripeInsufficientFundsDialog(this.isProd)
              .subscribe();
            this.subs.push(insufficientSub);
          } else {
            user.allUserPayouts[0].paid = false;
            user.allUserPayouts[0].paidDate = null;
            throw new Error(
              'An error has occurred while processing this payout in Stripe',
            );
          }
        });
      this.subs.push(payoutSub);
    }
  }

  sendStripeSignUpEmail(user: UserWithChartersAndPayouts) {
    const customerName = `${user.firstName} ${user.lastName}`;
    const payoutAmount = `$${user.netPay}`;

    const stripeSignupSub = this.payoutDao
      .sendRequestStripeSignUpEmail(customerName, payoutAmount, user.id)
      .subscribe((emailSendSuccess) => {});
    this.subs.push(stripeSignupSub);
  }

  requestStripeSignup(user: UserWithChartersAndPayouts) {
    const requestStripeSub = this.dialogService
      .requestStripeSignUpDialog()
      .subscribe((userResponse) => {
        if (userResponse === 'request') {
          this.sendStripeSignUpEmail(user);
        } else if (userResponse === 'paid') {
          this.markAsPaid(user);
          this.sendPaystubAndUpdatePayouts(user);
          const userIndex = this.unpaidUsers.findIndex(
            (found) => found.id === user.id,
          );
          this.unpaidUsers.splice(userIndex, 1);
        }
      });
    this.subs.push(requestStripeSub);
  }

  openSnackbar(message: string) {
    this.snackBar.open(message, null, {
      duration: 5000,
      verticalPosition: 'top',
    });
  }

  getPayoutName(payout: any) {
    let name: string;
    if (payout.type === 'owner') {
      name = 'Boat Rental plus Tax';
    } else if (payout.type === 'captain') {
      name = 'Captain Services';
    } else if (payout.type === 'crew') {
      name = 'Crew Services';
    } else if (payout.name === 'Fuel Included') {
      name = 'Prepaid Fuel';
    } else if (payout.name) {
      name = payout.name;
    }

    if (payout.processingFee === false) {
      name += '*';
    }

    return name;
  }

  private createPayoutLikeObjectsFromSubPayouts(
    payoutsWithSubPayouts: PayoutOrSubPayout,
  ) {
    payoutsWithSubPayouts.subPayouts.map((subPayout) => {
      if (subPayout.netPay === null || 'NaN') {
        if (subPayout.processingFee) {
          subPayout.netPay = (
            parseFloat(subPayout.actualPay) * this.GetTotalFromBase
          ).toFixed(2);
        } else {
          subPayout.netPay = parseFloat(subPayout.actualPay).toFixed(2);
        }
      }
      if (subPayout.type !== 'Anchor') {
        let newPayout = {
          ...payoutsWithSubPayouts,
          // @ts-ignore
          payee: `${subPayout.user.firstName} ${subPayout.user.lastName}`,
          // @ts-ignore
          payeePaypalHandle: subPayout.user.paypalEmail,
          actualPay: subPayout.actualPay,
          basePay: subPayout.basePay,
          netPay: subPayout.netPay,
          id: subPayout.id,
          notes: subPayout.notes,
          paid: subPayout.paid,
          paidDate: subPayout.paidDate,
          parentId: subPayout.parentId,
          paystubId: subPayout.paystubId,
          published: subPayout.published,
          userId: subPayout.userId,
          subPayouts: [],
          isSubPayout: true,
          processingFee: subPayout.processingFee,
          adminHold: subPayout.adminHold,
          systemHold: subPayout.systemHold,
          paidAtTheDock: subPayout.paidAtTheDock,
          prePaymentAmount: subPayout.prePaymentAmount,
          transferId: subPayout.transferId,
        };
        if (subPayout.adminHold || subPayout.systemHold) {
          this.payoutsWithSubPayoutsOnAdminHold.push(newPayout);
        } else {
          this.payoutsWithSubPayouts.push(newPayout);
        }
      }
    });
  }

  private sendPaystubAndUpdatePayouts(user) {
    const paystubSub = this.paystubDao
      .createPaystub(user.allUserPayouts, user.id)
      .subscribe((createdPaystub) => {
        const paystubPdfSub = this.paystubDao
          .createAndUploadPaystubPDF(user.id, user.allUserPayouts)
          .subscribe((key: string) => {
            // update paystub.pdfKey = key; and payouts to have paystubId
            this.updatePayoutsAndPaystubWithCreatedKey(
              createdPaystub,
              key,
              user,
            );
          });
        this.subs.push(paystubPdfSub);
      });
    this.subs.push(paystubSub);
  }

  private markAsPaid(user: UserWithChartersAndPayouts) {
    let updates = [];
    let subPayoutsUpdates = [];
    let newPayouts: Partial<Payout>[] = [];
    let newSubPayouts: SubPayout[] = [];

    user.allUserPayouts.forEach((payout) => {
      if (payout.paidAtTheDock) {
        if (payout.prePaymentAmount < parseFloat(payout.actualPay)) {
          if (payout.isSubPayout) {
            newSubPayouts.push({
              parentId: payout.parentId,
              userId: payout.userId,
              basePay: `${
                parseFloat(payout.actualPay) - payout.prePaymentAmount
              }`,
              actualPay: `${
                parseFloat(payout.actualPay) - payout.prePaymentAmount
              }`,
              netPay: null,
              paid: true,
              notes: payout.notes,
              published: true,
              paystubId: null,
              // @ts-ignore
              type: payout.type,
              processingFee: false,
              adminHold: payout.adminHold,
              systemHold: payout.systemHold,
              paidDate: new Date(),
              paidAtTheDock: false,
              transferId: null,
              prePaymentAmount: null,
            });
          } else {
            let copyPayout = payout;
            delete copyPayout.id;
            delete copyPayout.paystubId;
            delete copyPayout.userId;
            delete copyPayout.subPayouts;
            delete copyPayout.parentId;
            delete copyPayout.isSubPayout;
            delete copyPayout.charter;
            copyPayout = copyPayout as Payout;
            newPayouts.push({
              ...copyPayout,
              basePay: `${
                parseFloat(payout.actualPay) - payout.prePaymentAmount
              }`,
              actualPay: `${
                parseFloat(payout.actualPay) - payout.prePaymentAmount
              }`,
              netPay: null,
              prePaymentAmount: 0,
            });
          }
          payout.actualPay = `${payout.prePaymentAmount}`;
        }
      }

      if (payout.isSubPayout) {
        if (payout.paidAtTheDock) {
          subPayoutsUpdates.push({
            id: payout.id,
            paid: true,
            processingFee: payout.processingFee,
            actualPay: payout.actualPay,
            netPay: null,
          });
        } else {
          subPayoutsUpdates.push({
            id: payout.id,
            paid: true,
            paidDate: new Date(),
            processingFee: payout.processingFee,
            actualPay: payout.actualPay,
            netPay: null,
          });
        }
      } else {
        if (payout.paidAtTheDock) {
          updates.push({
            id: payout.id,
            paid: true,
            actualPay: payout.actualPay,
            netPay: null,
          });
        } else {
          updates.push({
            id: payout.id,
            paid: true,
            paidDate: new Date(),
            actualPay: payout.actualPay,
            netPay: null,
          });
        }
      }
    });

    console.log(newPayouts, newSubPayouts, updates, subPayoutsUpdates);

    if (newPayouts.length > 0) {
      const newPayoutsSub = this.payoutDao
        .createMultiple(newPayouts)
        .subscribe();
      this.subs.push(newPayoutsSub);
    }

    if (newSubPayouts.length > 0) {
      const newSubPayoutsSub = this.subPayoutDao
        .createMultiple(newSubPayouts)
        .subscribe();
      this.subs.push(newSubPayoutsSub);
    }

    const updateSub = this.payoutDao.updateMultiple(updates).subscribe();
    this.subs.push(updateSub);
    const updateSubPayoutsSub = this.subPayoutDao
      .updateMultiple(subPayoutsUpdates)
      .subscribe();
    this.subs.push(updateSubPayoutsSub);
  }

  private createUnpaidPayoutsByUserMap() {
    const unpaidUserIds = this.getUnpaidUserIdsFromPayouts();
    const allUserPayoutsMap = new Map();

    if (this.payoutsWithSubPayouts) {
      this.payoutsWithSubPayouts.forEach((payout: PayoutOrSubPayout) => {
        if (payout.payee !== 'Anchor' && !payout.paid) {
          const payoutsArray = allUserPayoutsMap.get(payout.userId) ?? [];
          allUserPayoutsMap.set(payout.userId, [...payoutsArray, payout]);
        }
      });
    }

    const userObservables = unpaidUserIds.map((userId) =>
      this.userDao.findUserWithStripeDetailsById(userId).pipe(take(1)),
    );

    forkJoin(userObservables).subscribe((users) => {
      users.forEach((user) => {
        user.allUserPayouts = allUserPayoutsMap.get(user.id);
        this.getCharterDataFromUserPayouts(user);
        this.getNetPayFromCharters(user);
        this.unpaidUsers.push(user);

        if (user.stripeConnectComplete) {
          this.readyToPay += user.netPay;
        }
      });

      this.loading = false;
    });
  }

  private getUnpaidUserIdsFromPayouts() {
    let unpaidUserIds = [];
    if (this.payoutsWithSubPayouts) {
      this.payoutsWithSubPayouts.forEach((payout: PayoutOrSubPayout) => {
        if (
          !unpaidUserIds.includes(payout.userId) &&
          !!payout.userId &&
          payout.payee !== 'Anchor' &&
          !payout.paid
        ) {
          unpaidUserIds.push(payout.userId);
        }
      });
    }
    return unpaidUserIds;
  }

  private getCharterDataFromUserPayouts(user: UserWithChartersAndPayouts) {
    const charterPayoutsMap = new Map();
    user.allUserCharterIds = [];
    user.userCharterData = [];
    if (user.allUserPayouts) {
      user.allUserPayouts.forEach((payout) => {
        if (!user.allUserCharterIds.includes(payout.charterId)) {
          user.allUserCharterIds.push(payout.charterId);
          user.userCharterData.push(payout.charter);
        }

        const chartersArray = charterPayoutsMap.get(payout.charterId) ?? [];
        charterPayoutsMap.set(payout.charterId, [
          ...chartersArray,
          { ...payout },
        ]);
      });
      this.getCharterTotals(user, charterPayoutsMap);
    }
  }

  private getCharterTotals(
    user: UserWithChartersAndPayouts,
    charterPayoutsMap,
  ) {
    user.userCharterData.forEach((charter: Partial<CharterWithTotals>) => {
      charter.userCharterTotal = 0;
      charter.userCharterProcessingFee = 0;

      const charterPayouts = charterPayoutsMap.get(charter.id);
      charterPayouts.forEach((payout) => {
        charter.userCharterTotal += Number(payout.actualPay);
        if (payout.processingFee) {
          charter.userCharterProcessingFee += Number(
            (Number(payout.actualPay) * this.Base).toFixed(2),
          );
        }
      });

      charter.userCharterNet =
        charter.userCharterTotal - charter.userCharterProcessingFee;
    });
  }

  private getNetPayFromCharters(user: UserWithChartersAndPayouts) {
    user.netPay = 0;
    user.userCharterData.forEach((charter: Partial<CharterWithTotals>) => {
      user.netPay += charter.userCharterNet;
    });
    user.netPay = Number(user.netPay.toFixed(2));
  }

  private updatePayoutsAndPaystubWithCreatedKey(
    createdPaystub: any,
    key: string,
    user: UserWithChartersAndPayouts,
  ) {
    let updatedPayouts = [];
    const updatedSubPayouts: SubPayout[] = [];
    user.allUserPayouts.forEach((userPayout: any) => {
      userPayout.paystubId = createdPaystub.id;
      userPayout.paid = true;
      userPayout.paidDate = new Date();
      if (!userPayout.isSubPayout) {
        updatedPayouts.push(userPayout);
      } else {
        updatedSubPayouts.push(userPayout);
      }
    });
    const paystubPayoutSub = this.payoutDao
      .updateMultiple(updatedPayouts)
      .subscribe();
    this.subs.push(paystubPayoutSub);
    const paystubSubPayoutSub = this.subPayoutDao
      .updateMultiple(updatedSubPayouts)
      .subscribe();
    this.subs.push(paystubSubPayoutSub);
    createdPaystub.pdfKey = key;
    const paystubKeySub = this.paystubDao
      .updateFromPartial(createdPaystub.id, createdPaystub)
      .subscribe();
    this.subs.push(paystubKeySub);
  }

  getCharterNet(allUserPayouts: any, charterId: number) {
    let charterTotal = 0;

    allUserPayouts.forEach((payout) => {
      if (payout.charterId === charterId) {
        if (payout.processingFee) {
          charterTotal += Number(
            (Number(payout.actualPay) * this.GetTotalFromBase).toFixed(2),
          );
        } else {
          charterTotal += Number(payout.actualPay);
        }
      }
    });

    return charterTotal.toFixed(2);
  }

  getCharterProcessingFee(allUserPayouts: any, charterId: number) {
    let charterProcessingFee = 0;

    allUserPayouts.forEach((payout) => {
      if (payout.charterId === charterId) {
        if (payout.processingFee) {
          charterProcessingFee += Number(
            (Number(payout.actualPay) * this.Base).toFixed(2),
          );
        }
      }
    });

    return charterProcessingFee.toFixed(2);
  }

  navToCharterDetails(payout): void {
    const queryParams = { queryParams: { index: '5' } };
    this.router.navigate(
      [`manage/manage-charter/${payout.charterId}`],
      queryParams,
    );
  }

  approvePayout(payout) {
    const chickenSub = this.chickenTestDialog
      .open(
        `Are you sure you want to remove the hold?`,
        'Yes, approve',
        'No, go back',
        25,
        null,
        'Approve Payout',
        null,
        `${payout.payee}'s payout of $${payout.actualPay} will be published immediately.`,
        true,
      )
      .subscribe((result) => {
        console.log(payout);
        if (result) {
          this.payoutsWithSubPayoutsOnAdminHold = [];
          this.payoutsWithSubPayouts = [];
          this.unpaidUsers = [];
          this.unpaidUserIds = [];
          this.readyToPay = 0.0;
          if (payout.isSubPayout) {
            const updateSub = this.subPayoutDao
              .updateFromPartial(payout.id, {
                adminHold: false,
                systemHold: null,
              })
              .subscribe(() => {
                this.loadPayouts();
              });
            this.subs.push(updateSub);
          } else {
            const updateSub = this.payoutDao
              .updateFromPartial(payout.id, {
                adminHold: false,
                systemHold: null,
              })
              .subscribe(() => {
                this.loadPayouts();
              });
            this.subs.push(updateSub);
          }
        }
      });
    this.subs.push(chickenSub);
  }

  setPayoutBeingEdited(payout: any) {
    this.payoutBeingEdited = JSON.parse(JSON.stringify(payout));
  }

  updateHeldPayout() {
    const updatedPayout: Partial<SubPayout> = {
      id: this.payoutBeingEdited.id,
      userId: this.payoutBeingEdited.userId,
      basePay: this.payoutBeingEdited.basePay,
      actualPay: this.payoutBeingEdited.actualPay,
      notes: this.payoutBeingEdited.notes,
      processingFee: this.payoutBeingEdited.processingFee,
      netPay: null,
    };
    if (this.payoutBeingEdited.isSubPayout) {
      let subPayoutIndex = this.payoutsWithSubPayoutsOnAdminHold.findIndex(
        (payout) => {
          return payout.id === updatedPayout.id && payout.isSubPayout;
        },
      );

      this.payoutsWithSubPayoutsOnAdminHold[subPayoutIndex] =
        this.payoutBeingEdited;

      const subPayoutSub = this.subPayoutDao
        .updateFromPartial(this.payoutBeingEdited.id, updatedPayout)
        .subscribe(() => {
          this.payoutBeingEdited = null;
        });
      this.subs.push(subPayoutSub);
    } else {
      let payoutIndex = this.payoutsWithSubPayoutsOnAdminHold.findIndex(
        (payout) => {
          return payout.id === updatedPayout.id;
        },
      );
      this.payoutsWithSubPayoutsOnAdminHold[payoutIndex] =
        this.payoutBeingEdited;

      const subPayoutSub = this.payoutDao
        .updateFromPartial(this.payoutBeingEdited.id, updatedPayout)
        .subscribe(() => {
          this.payoutBeingEdited = null;
        });
      this.subs.push(subPayoutSub);
    }
  }

  cancelEdit() {
    this.payoutBeingEdited = null;
  }

  getUserValidPaymentStatus(userId) {
    return this.unpaidUsers.find((x) => x.id === userId)?.hasValidPaymentMethod;
  }
}
//todo remove
export interface PayoutOrSubPayout extends Payout {
  paystubId: number;
  userId: number;
  subPayouts?: SubPayout[];
  parentId?: number;
  isSubPayout?: boolean;
  charter?: Charter;
}

export interface UserWithChartersAndPayouts extends User {
  allUserPayouts: Partial<PayoutOrSubPayout>[];
  allUserCharterIds: number[];
  userCharterData: Partial<CharterWithTotals>[];
  netPay: number;
  userPaymentDetails?: UserPaymentDetail;
}

export interface CharterWithTotals extends Partial<Charter> {
  userCharterTotal: number;
  userCharterNet: number;
  userCharterProcessingFee: number;
}
